import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { timeout } from 'rxjs/operators';

type Paso = 'cargando' | 'datos' | 'confirmar' | 'codigo' | 'exito' | 'activa' | 'cobro' | 'error';

interface PlanResumen {
    id: number;
    nombre: string;
    precio: number;
    moneda: string;
    periodo: string;
}

/**
 * Firma del convenio de Agreement Pay dentro del mini-program de Toka.
 *
 * El usuario autoriza una sola vez con un código SMS y a partir de ahí el cobro
 * mensual se hace sin que vuelva a intervenir.
 */
@Component({
    selector: 'msc-toka-firma',
    templateUrl: './toka-firma.component.html',
    styleUrls: ['./toka-firma.component.scss'],
})
export class TokaFirmaComponent implements OnInit, OnDestroy {

    public paso: Paso = 'cargando';
    public plan: PlanResumen | null = null;
    public telefono: string | null = null;
    public montoAutorizado: number | null = null;
    public mensajeError = '';
    public enviando = false;

    public codigo = '';
    public segundosRestantes = 0;

    // Plan B si el bridge no responde: el usuario teclea sus datos. Toka los
    // contrasta contra su registro, así que tienen que coincidir exactamente.
    public manual = { first_surname: '', second_surname: '', name: '' };
    public mostrarManual = false;

    private contador: any = null;
    private readonly api = `${environment.envVar.API_URL}/toka/agreement`;

    constructor(private http: HttpClient) {}

    ngOnInit(): void {
        this.cargarEstado();
    }

    ngOnDestroy(): void {
        this.detenerContador();
    }

    private cargarEstado(): void {
        this.http.get(this.api).pipe(timeout(15000)).subscribe({
            next: (r: any) => {
                this.plan = r?.plan ?? null;
                this.telefono = r?.telefono ?? null;

                // El convenio puede estar firmado y el cobro haber fallado. En
                // ese caso no se puede volver a firmar (Toka daría 32000002):
                // solo cabe reintentar el cobro.
                if (r?.pagoPendiente) {
                    this.paso = 'cobro';
                    return;
                }

                if (r?.tieneConvenio) {
                    this.paso = 'activa';
                    return;
                }

                this.paso = r?.faltanApellidos ? 'datos' : 'confirmar';
            },
            error: () => this.fallar('No pudimos cargar tu información. Inténtalo de nuevo.'),
        });
    }

    /**
     * Pide los apellidos a Toka. Solo devuelve un authCode: los datos los canjea
     * el backend, que es el único que puede firmar la llamada.
     */
    public obtenerDatosDeToka(): void {
        const bridge = (window as any).AlipayJSBridge;

        if (!bridge) {
            this.mostrarManual = true;
            return;
        }

        this.enviando = true;
        this.mensajeError = '';

        // Guardián: si el callback del bridge no vuelve, el botón se quedaría en
        // "Consultando…" para siempre y el usuario sin salida. Ya nos pasó con
        // este mismo JSAPI durante el login.
        let respondio = false;
        const guardian = setTimeout(() => {
            if (respondio) {
                return;
            }

            respondio = true;
            this.enviando = false;
            this.mostrarManual = true;
            this.mensajeError = 'Toka no respondió. Puedes escribir tus datos a mano.';
        }, 10000);

        try {
            bridge.call(
                'getUserPersonalInformationAuthCode',
                {
                    scopes: ['USER_NAME', 'USER_FIRST_SURNAME', 'USER_SECOND_SURNAME'],
                    usage: 'Firmar tu suscripción mensual',
                },
                (data: any) => {
                    if (respondio) {
                        return;
                    }

                    respondio = true;
                    clearTimeout(guardian);
                    this.enviando = false;

                    if (!data || data.resultCode !== 10000 || !data.result) {
                        // No se bloquea el flujo: se ofrece teclearlos.
                        this.mostrarManual = true;
                        this.mensajeError = data?.resultMsg
                            ? `Toka respondió: ${data.resultMsg}`
                            : 'No pudimos obtener tus datos de Toka.';
                        return;
                    }

                    this.solicitarCodigo(data.result);
                }
            );
        } catch (e: any) {
            respondio = true;
            clearTimeout(guardian);
            this.enviando = false;
            this.mostrarManual = true;
            this.mensajeError = 'No pudimos consultar tus datos en Toka.';
        }
    }

    public continuarConDatosManuales(): void {
        if (!this.manual.first_surname || !this.manual.name) {
            this.mensajeError = 'Necesitamos tu nombre y tu apellido paterno.';
            return;
        }

        this.solicitarCodigo(null);
    }

    public solicitarCodigo(personalAuthCode: string | null): void {
        this.enviando = true;
        this.mensajeError = '';

        const cuerpo: any = {};

        if (personalAuthCode) {
            cuerpo.personalAuthCode = personalAuthCode;
        } else if (this.mostrarManual) {
            cuerpo.firstSurname = this.manual.first_surname;
            cuerpo.secondSurname = this.manual.second_surname;
            cuerpo.name = this.manual.name;
        }

        this.http.post(`${this.api}/sms`, cuerpo).pipe(timeout(20000)).subscribe({
            next: (r: any) => {
                this.enviando = false;
                this.montoAutorizado = r?.montoAutorizado ?? null;
                this.paso = 'codigo';
                this.codigo = '';
                this.arrancarContador(r?.vigenciaSegundos ?? 90);
            },
            error: (e: any) => {
                this.enviando = false;
                this.mensajeError = this.leerError(e, 'No pudimos enviarte el código.');
            },
        });
    }

    public firmar(): void {
        if (this.codigo.trim().length === 0) {
            this.mensajeError = 'Escribe el código que te llegó por SMS.';
            return;
        }

        this.enviando = true;
        this.mensajeError = '';

        this.http.post(`${this.api}/sign`, { smsCode: this.codigo.trim() })
            .pipe(timeout(30000))
            .subscribe({
                next: () => {
                    this.enviando = false;
                    this.detenerContador();
                    this.paso = 'exito';
                },
                error: (e: any) => {
                    this.enviando = false;
                    this.mensajeError = this.leerError(e, 'No pudimos validar el código.');
                },
            });
    }

    public reenviar(): void {
        if (this.segundosRestantes > 0) {
            return;
        }

        this.solicitarCodigo(null);
    }

    /**
     * Reintenta el cobro sin volver a firmar: el convenio sigue vigente.
     */
    public reintentarCobro(): void {
        this.enviando = true;
        this.mensajeError = '';

        this.http.post(`${this.api}/charge`, {}).pipe(timeout(30000)).subscribe({
            next: () => {
                this.enviando = false;
                this.paso = 'exito';
            },
            error: (e: any) => {
                this.enviando = false;
                this.mensajeError = this.leerError(e, 'No pudimos procesar el cobro.');
            },
        });
    }

    public cancelar(): void {
        this.enviando = true;

        this.http.delete(this.api).pipe(timeout(20000)).subscribe({
            next: () => {
                this.enviando = false;
                this.paso = 'confirmar';
            },
            error: (e: any) => {
                this.enviando = false;
                this.mensajeError = this.leerError(e, 'No pudimos cancelar la suscripción.');
            },
        });
    }

    public irAlInicio(): void {
        window.location.href = '/';
    }

    /**
     * Cuenta regresiva de los 90 s de vigencia del código, para que el usuario
     * sepa cuándo puede pedir otro y no agote el tope diario probando.
     */
    private arrancarContador(segundos: number): void {
        this.detenerContador();
        this.segundosRestantes = segundos;

        this.contador = setInterval(() => {
            this.segundosRestantes -= 1;

            if (this.segundosRestantes <= 0) {
                this.detenerContador();
            }
        }, 1000);
    }

    private detenerContador(): void {
        if (this.contador !== null) {
            clearInterval(this.contador);
            this.contador = null;
        }

        this.segundosRestantes = 0;
    }

    private leerError(e: any, porDefecto: string): string {
        if (e?.name === 'TimeoutError') {
            return 'El servidor tardó demasiado en responder. Inténtalo de nuevo.';
        }

        return e?.error?.message || porDefecto;
    }

    private fallar(mensaje: string): void {
        this.paso = 'error';
        this.mensajeError = mensaje;
    }
}
